import { useSelector } from '@xstate/react';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { selectActiveTab, selectModal } from '../selectors';
import { LICENSES_TABS, useLicensesActorRef } from '../utils';

/**
 * Service hook for shared licenses concerns: tab switching, modal state.
 * Used by the LicensesForm orchestrator component.
 */
export function useLicensesService() {
  const location = useLocation();
  const navigate = useNavigate();
  const licensesRef = useLicensesActorRef();

  // ── Narrow selectors — each re-renders only when its field changes ──
  const activeTab = useSelector(licensesRef, selectActiveTab);
  const modal = useSelector(licensesRef, selectModal);

  // ── Sync URL hash → machine when hash changes (sidebar nav, deep links) ─
  // No hash (or an unknown one) means the first tab: the machine outlives
  // this page, so without the reset a stale activeTab from a previous visit
  // would win when navigating here from the dashboard (MCV-1464).
  useEffect(() => {
    const hashTab = location.hash.replace('#', '');
    const hashIndex = LICENSES_TABS.findIndex((t) => t.type === hashTab);
    const targetIndex = hashIndex === -1 ? 0 : hashIndex;
    if (targetIndex !== activeTab) {
      licensesRef.send({ type: 'ON_SET_ACTIVE_TAB', index: targetIndex });
    }
  }, [activeTab, licensesRef, location.hash]);

  // ── Derived state (machine is single source of truth) ─────────────
  const currentTab = LICENSES_TABS[activeTab] ?? LICENSES_TABS[0];
  const currentTabType = currentTab.type;
  const isDeaTab = currentTabType === 'dea';

  // ── Stable callbacks ────────────────────────────────────────────────
  // Only navigate: the hash is the single source of truth and the effect
  // above syncs the machine from it. Sending ON_SET_ACTIVE_TAB here too
  // creates a render where the machine is ahead of the still-async router
  // update, which the effect would "correct" by bouncing back to the old tab.
  const handleTabChange = useCallback(
    (index: number) => {
      navigate({ hash: LICENSES_TABS[index].type }, { replace: true });
    },
    [navigate]
  );

  const handleOnClose = useCallback(
    () => licensesRef.send({ type: 'ON_CANCEL_EDIT_OR_CREATE' }),
    [licensesRef]
  );

  return {
    activeTab,
    modal,
    currentTabType,
    isDeaTab,
    handleTabChange,
    handleOnClose,
  };
}
